import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const API_URL = import.meta.env.VITE_API_URL;

// ─── Hook ──────────────────────────────────────────────────────

/**
 * useRsvpFlow
 *
 * Single source of truth for the RSVP modal. Owns all state for both
 * flows (coded RSVP + request-to-attend), the manual code entry screen,
 * and the choice screen. Fetches run only while `open` is true so we
 * don't eagerly hit the API.
 *
 * Important contract invariants preserved from the legacy CtaSection
 * implementation:
 *   - `new-0` / `new-K` ordering in the request submission.
 *   - `_hp` is always a string (empty for real users, never undefined).
 *   - `newMembers` with whitespace-only names are filtered at submit.
 *   - Codes are uppercased before the GET.
 *   - `alreadySubmitted` short-circuits matrix initialization.
 *   - When an extra is added in the request flow, their responses default
 *     to `"attending"` for every section.
 *
 * @param {boolean} open
 * @param {{ eventId: string | null, rsvpCode: string | null }} config
 */
export default function useRsvpFlow(open, { eventId, rsvpCode }) {
  // Read the active language so we can pass `?lang=` to the BE. Only the
  // demo invite branch on the server honours this; real events ignore it.
  const { i18n } = useTranslation();
  const shortLang = (i18n.resolvedLanguage || i18n.language || "en").startsWith("el")
    ? "el"
    : "en";
  // ─── Flow control ─────────────────────────────────────────────
  const [mode, setMode] = useState(rsvpCode ? "code" : "choice");
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ─── Coded flow ────────────────────────────────────────────────
  const [rsvpData, setRsvpData] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [responses, setResponses] = useState([]);
  const [newMembers, setNewMembers] = useState([]);
  const [submissionSummary, setSubmissionSummary] = useState(null);
  const [activeCode, setActiveCode] = useState(rsvpCode);
  const [codedMessage, setCodedMessage] = useState("");

  // ─── Manual code entry ────────────────────────────────────────
  const [manualCode, setManualCode] = useState("");

  // ─── Request-to-attend ────────────────────────────────────────
  const [reqSections, setReqSections] = useState([]);
  const [reqFirstName, setReqFirstName] = useState("");
  const [reqLastName, setReqLastName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqPhone, setReqPhone] = useState("");
  const [reqMessage, setReqMessage] = useState("");
  const [reqExtraMembers, setReqExtraMembers] = useState([]);
  const [reqResponses, setReqResponses] = useState([]);
  const [reqSubmitted, setReqSubmitted] = useState(false);
  // True when the submit response carried `demo: true` (reserved demo
  // event short-circuit on the BE). Lets the Done screen swap copy and
  // CTA without affecting any real RSVP path. Captured separately from
  // `event.isDemo` because the BE is the source of truth for whether
  // the submission was actually persisted.
  const [reqDemoSubmission, setReqDemoSubmission] = useState(false);
  const [reqHoneypot, setReqHoneypot] = useState("");
  const [reqTurnstileToken, setReqTurnstileToken] = useState(null);

  // ─── Reset ─────────────────────────────────────────────────────
  const reset = useCallback(() => {
    // Preserve activeCode if it was pre-seeded so a re-open of a coded
    // invitation lands straight back in the coded flow.
    setMode(rsvpCode ? "code" : "choice");
    setStep(0);
    setLoading(false);
    setError(null);
    setSubmitting(false);
    setRsvpData(null);
    setAlreadySubmitted(false);
    setResponses([]);
    setNewMembers([]);
    setSubmissionSummary(null);
    setActiveCode(rsvpCode);
    setCodedMessage("");
    setManualCode("");
    setReqSections([]);
    setReqFirstName("");
    setReqLastName("");
    setReqEmail("");
    setReqPhone("");
    setReqMessage("");
    setReqExtraMembers([]);
    setReqResponses([]);
    setReqSubmitted(false);
    setReqDemoSubmission(false);
    setReqHoneypot("");
    setReqTurnstileToken(null);
  }, [rsvpCode]);

  // Leave the coded flow (e.g. "That's not me" on the identity step, or
  // recovering from a failed code fetch). Drops all coded-flow state
  // and the active code so we don't re-fetch the same invitation, then
  // lands the user on the ChoiceScreen so they can try a different code
  // or send a request-to-attend.
  const leaveCodedFlow = useCallback(() => {
    setMode("choice");
    setStep(0);
    setError(null);
    setActiveCode(null);
    setRsvpData(null);
    setAlreadySubmitted(false);
    setResponses([]);
    setNewMembers([]);
    setSubmissionSummary(null);
    setCodedMessage("");
  }, []);

  // Centralized mode-switch that also clears any lingering error from
  // the previous flow. Every call site in the UI uses this instead of
  // raw setMode so stale errors don't leak across screens (e.g. a
  // wrong-code error carrying into the request flow).
  const changeMode = useCallback((nextMode) => {
    setError(null);
    setStep(0);
    setMode(nextMode);
  }, []);

  // ─── Effects ──────────────────────────────────────────────────

  // Fetch coded RSVP data.
  useEffect(() => {
    if (!open || mode !== "code" || !activeCode || !eventId) return undefined;
    let cancelled = false;
    const fetchRsvp = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_URL}/api/events/${eventId}/rsvp/${activeCode}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "Could not load your RSVP");
        }
        const data = await res.json();
        if (cancelled) return;
        setRsvpData(data);
        setAlreadySubmitted(data.alreadySubmitted);
        if (!data.alreadySubmitted) {
          const initial = [];
          for (const member of data.party.members) {
            for (const section of data.sections) {
              initial.push({
                memberId: member._id,
                sectionId: section._id,
                status: "pending",
              });
            }
          }
          setResponses(initial);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRsvp();
    return () => {
      cancelled = true;
    };
  }, [open, mode, activeCode, eventId]);

  // Fetch public sections for the request flow + seed the "self" response rows.
  useEffect(() => {
    if (!open || mode !== "request" || !eventId) return undefined;
    if (reqSections.length > 0) return undefined;
    let cancelled = false;
    const fetchSections = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/events/${eventId}/rsvp-sections?lang=${shortLang}`,
        );
        if (!res.ok) throw new Error("Could not load event sections");
        const data = await res.json();
        if (cancelled) return;
        const sections = data.sections || [];
        setReqSections(sections);
        setReqResponses(
          sections.map((s) => ({
            memberId: "self",
            sectionId: s._id,
            status: "pending",
          })),
        );
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    fetchSections();
    return () => {
      cancelled = true;
    };
    // shortLang is intentionally NOT a dep — only the demo slug honours it,
    // and refetching on language toggle would reset the RSVP flow state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, eventId, reqSections.length]);

  // Keep request-flow responses in sync with extra-member additions/removals.
  // Extras start as "pending" so the user has to pick attendance
  // explicitly — we don't preselect answers on their behalf.
  useEffect(() => {
    if (mode !== "request" || reqSections.length === 0) return;
    setReqResponses((prev) => {
      const next = [...prev];
      reqExtraMembers.forEach((_, idx) => {
        const id = `extra-${idx}`;
        for (const section of reqSections) {
          const exists = next.find(
            (r) => r.memberId === id && r.sectionId === section._id,
          );
          if (!exists) {
            next.push({
              memberId: id,
              sectionId: section._id,
              status: "pending",
            });
          }
        }
      });
      const validIds = new Set([
        "self",
        ...reqExtraMembers.map((_, idx) => `extra-${idx}`),
      ]);
      return next.filter((r) => validIds.has(r.memberId));
    });
  }, [reqExtraMembers, reqSections, mode]);

  // When a new member is added (coded flow), seed response entries for
  // them at "pending" for every section. The user has to pick explicitly
  // — we don't assume attendance on their behalf.
  useEffect(() => {
    if (!rsvpData) return;
    const sections = rsvpData.sections;
    setResponses((prev) => {
      const updated = [...prev];
      newMembers.forEach((_, i) => {
        const placeholderId = `new-${i}`;
        for (const section of sections) {
          const exists = updated.find(
            (r) =>
              r.memberId === placeholderId && r.sectionId === section._id,
          );
          if (!exists) {
            updated.push({
              memberId: placeholderId,
              sectionId: section._id,
              status: "pending",
            });
          }
        }
      });
      return updated;
    });
  }, [newMembers, rsvpData]);

  // ─── Actions — coded flow ─────────────────────────────────────

  const setStatus = useCallback((memberId, sectionId, nextStatus) => {
    setResponses((prev) => {
      const existing = prev.find(
        (r) => r.memberId === memberId && r.sectionId === sectionId,
      );
      if (existing) {
        return prev.map((r) =>
          r === existing ? { ...r, status: nextStatus } : r,
        );
      }
      return [...prev, { memberId, sectionId, status: nextStatus }];
    });
  }, []);

  const addNewMember = useCallback(() => {
    setNewMembers((prev) => [
      ...prev,
      { name: "", role: "partner", ageCategory: "unspecified" },
    ]);
  }, []);

  const updateNewMember = useCallback((index, field, value) => {
    setNewMembers((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    );
  }, []);

  const removeNewMember = useCallback((index) => {
    setNewMembers((prev) => prev.filter((_, i) => i !== index));
    const placeholderId = `new-${index}`;
    setResponses((prev) => prev.filter((r) => r.memberId !== placeholderId));
  }, []);

  const submitCoded = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Drop any unnamed new members and the responses that point at
      // them. The `isCodedAttendanceComplete` guard should prevent this
      // branch from ever running with empty names, but we belt-and-brace
      // the payload to stay safe.
      const keptNewIds = new Set(
        newMembers
          .map((m, i) => (m.name.trim() ? `new-${i}` : null))
          .filter(Boolean),
      );
      const cleanedResponses = responses.filter((r) => {
        if (!r.memberId?.startsWith("new-")) return true;
        return keptNewIds.has(r.memberId);
      });
      const trimmedMessage = codedMessage.trim();
      const body = {
        responses: cleanedResponses,
        newMembers: newMembers
          .filter((m) => m.name.trim())
          .map((m) => ({
            name: m.name.trim(),
            role: m.role,
            ageCategory: m.ageCategory,
          })),
        ...(trimmedMessage ? { answers: { message: trimmedMessage } } : {}),
      };
      const res = await fetch(
        `${API_URL}/api/events/${eventId}/rsvp/${activeCode}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to submit RSVP");
      }
      const data = await res.json();
      // The server echoes `summary` with real DB _ids for new members, but
      // `allMembers` on the confirmation step still carries the frontend
      // placeholder ids ("new-0", "new-1", ...). Without a remap, the
      // (member × response) join in RsvpRecap silently drops new-member rows
      // because the ids don't match, producing a misleading recap.
      //
      // The server preserves row order in its echo, so walk the pre-submit
      // array and the server echo in parallel: any row whose original
      // memberId is a "new-N" placeholder gets its echoed real id mapped
      // back to that placeholder.
      const remappedSummary = Array.isArray(data.summary)
        ? data.summary.map((r, i) => {
            const original = cleanedResponses[i];
            if (original?.memberId?.startsWith("new-") && r.memberId !== original.memberId) {
              return { ...r, memberId: original.memberId };
            }
            return r;
          })
        : [];
      setSubmissionSummary(remappedSummary);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [responses, newMembers, codedMessage, eventId, activeCode]);

  // ─── Actions — manual code entry ──────────────────────────────

  const submitManualCode = useCallback(async () => {
    const code = manualCode.trim().toUpperCase();
    if (!code) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/events/${eventId}/rsvp/${code}`,
      );
      if (!res.ok) {
        throw new Error(
          "We couldn't find an invitation for that code. Please double-check, or send a request to attend.",
        );
      }
      setActiveCode(code);
      setMode("code");
      setStep(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [manualCode, eventId]);

  // ─── Actions — request-to-attend ──────────────────────────────

  const addReqExtraMember = useCallback(() => {
    setReqExtraMembers((prev) => [...prev, { name: "" }]);
  }, []);

  const updateReqExtraMember = useCallback((index, name) => {
    setReqExtraMembers((prev) =>
      prev.map((m, i) => (i === index ? { ...m, name } : m)),
    );
  }, []);

  const removeReqExtraMember = useCallback((index) => {
    setReqExtraMembers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const setReqStatus = useCallback((memberId, sectionId, nextStatus) => {
    setReqResponses((prev) => {
      const existing = prev.find(
        (r) => r.memberId === memberId && r.sectionId === sectionId,
      );
      if (existing) {
        return prev.map((r) =>
          r === existing ? { ...r, status: nextStatus } : r,
        );
      }
      return [...prev, { memberId, sectionId, status: nextStatus }];
    });
  }, []);

  const canProceedReqStep1 = Boolean(
    reqFirstName.trim() &&
      reqLastName.trim() &&
      (reqEmail.trim() || reqPhone.trim()),
  );

  const submitRequest = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const cleanedExtras = reqExtraMembers
        .map((m) => m.name.trim())
        .filter(Boolean);
      const members = [
        { name: `${reqFirstName.trim()} ${reqLastName.trim()}`.trim() },
        ...cleanedExtras.map((name) => ({ name })),
      ];
      // Map our "self" / "extra-N" ids to backend "new-0" / "new-K".
      const idMap = { self: "new-0" };
      cleanedExtras.forEach((_, i) => {
        idMap[`extra-${i}`] = `new-${i + 1}`;
      });
      const mappedResponses = reqResponses
        .filter((r) => idMap[r.memberId])
        .map((r) => ({
          memberId: idMap[r.memberId],
          sectionId: r.sectionId,
          status: r.status,
        }));

      const body = {
        firstName: reqFirstName.trim(),
        lastName: reqLastName.trim(),
        email: reqEmail.trim() || undefined,
        phone: reqPhone.trim() || undefined,
        message: reqMessage.trim() || undefined,
        members,
        responses: mappedResponses,
        _hp: reqHoneypot, // must be "" not undefined — the backend's silent-204 rule only triggers when non-empty
        ...(reqTurnstileToken ? { turnstileToken: reqTurnstileToken } : {}),
      };
      const res = await fetch(
        `${API_URL}/api/events/${eventId}/rsvp/request`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Could not send your request");
      }
      // Capture `demo: true` so the Done screen can show preview-only
      // copy. The BE short-circuits writes for the reserved demo slug
      // (see api/routes/rsvp.routes.mjs), and this flag is the only way
      // the FE can distinguish "we saved this" from "we pretended to".
      const responseBody = await res.json().catch(() => ({}));
      setReqDemoSubmission(responseBody?.demo === true);
      setReqSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [
    reqExtraMembers,
    reqFirstName,
    reqLastName,
    reqEmail,
    reqPhone,
    reqMessage,
    reqResponses,
    reqHoneypot,
    reqTurnstileToken,
    eventId,
  ]);

  // ─── Derived — members + progress detection ───────────────────

  const allMembers = useMemo(
    () => [
      ...(rsvpData?.party?.members || []),
      ...newMembers.map((m, i) => ({
        _id: `new-${i}`,
        // Display fallback for the read-only case; the matrix also
        // receives the raw typed value so the inline input doesn't
        // show the placeholder as if the user had typed it.
        name: m.name || `Guest ${i + 1}`,
        rawName: m.name,
        role: m.role,
        isNew: true,
      })),
    ],
    [rsvpData, newMembers],
  );

  const reqAllMembers = useMemo(() => {
    const selfName =
      `${reqFirstName.trim()} ${reqLastName.trim()}`.trim() || "You";
    return [
      { _id: "self", name: selfName },
      ...reqExtraMembers.map((m, i) => ({
        _id: `extra-${i}`,
        name: m.name?.trim() || `Guest ${i + 1}`,
        isNew: true,
      })),
    ];
  }, [reqFirstName, reqLastName, reqExtraMembers]);

  // Attendance completion gates. Submit is only enabled when every
  // (member × section) row carries an explicit status (not "pending").
  // The `touched` flag is for the helper-text UX: we only nag the user
  // after they've started picking, so a fresh screen stays quiet.
  const isCodedAttendanceComplete = useMemo(() => {
    if (!rsvpData || responses.length === 0) return false;
    if (!responses.every((r) => r.status && r.status !== "pending")) {
      return false;
    }
    // If the user added extras but left any of them unnamed, block submit
    // so we don't ship placeholder "Guest N" rows to the server.
    if (newMembers.some((m) => !m.name.trim())) return false;
    return true;
  }, [rsvpData, responses, newMembers]);

  const hasCodedAttendanceStarted = useMemo(
    () => responses.some((r) => r.status && r.status !== "pending"),
    [responses],
  );

  const isReqAttendanceComplete = useMemo(() => {
    if (reqSections.length === 0 || reqResponses.length === 0) return false;
    return reqResponses.every((r) => r.status && r.status !== "pending");
  }, [reqSections.length, reqResponses]);

  const hasReqAttendanceStarted = useMemo(
    () => reqResponses.some((r) => r.status && r.status !== "pending"),
    [reqResponses],
  );

  // Used by the modal to decide whether to prompt before discarding.
  const hasUnsavedProgress = useMemo(() => {
    if (submitting) return false;
    if (mode === "code") {
      if (alreadySubmitted) return false;
      if (step === 3) return false; // submitted summary
      const dirtyResponse = responses.some((r) => r.status !== "pending");
      const dirtyMember = newMembers.some((m) => m.name.trim());
      const dirtyMessage = Boolean(codedMessage.trim());
      return dirtyResponse || dirtyMember || dirtyMessage;
    }
    if (mode === "request") {
      if (reqSubmitted) return false;
      const dirtyIdentity =
        reqFirstName.trim() ||
        reqLastName.trim() ||
        reqEmail.trim() ||
        reqPhone.trim() ||
        reqMessage.trim();
      const dirtyResponses = reqResponses.some(
        (r) => r.status && r.status !== "pending",
      );
      const dirtyExtras = reqExtraMembers.some((m) => m.name?.trim());
      return Boolean(dirtyIdentity || dirtyResponses || dirtyExtras);
    }
    if (mode === "codeEntry") {
      return Boolean(manualCode.trim());
    }
    return false;
  }, [
    submitting,
    mode,
    step,
    alreadySubmitted,
    responses,
    newMembers,
    codedMessage,
    reqSubmitted,
    reqFirstName,
    reqLastName,
    reqEmail,
    reqPhone,
    reqMessage,
    reqResponses,
    reqExtraMembers,
    manualCode,
  ]);

  return {
    // state
    mode,
    step,
    loading,
    submitting,
    error,
    rsvpData,
    alreadySubmitted,
    responses,
    newMembers,
    submissionSummary,
    activeCode,
    codedMessage,
    manualCode,
    reqSections,
    reqFirstName,
    reqLastName,
    reqEmail,
    reqPhone,
    reqMessage,
    reqExtraMembers,
    reqResponses,
    reqSubmitted,
    reqDemoSubmission,
    reqHoneypot,
    reqTurnstileToken,
    // derived
    allMembers,
    reqAllMembers,
    canProceedReqStep1,
    isCodedAttendanceComplete,
    hasCodedAttendanceStarted,
    isReqAttendanceComplete,
    hasReqAttendanceStarted,
    hasUnsavedProgress,
    // actions
    actions: {
      setMode,
      changeMode,
      setStep,
      reset,
      leaveCodedFlow,
      setStatus,
      addNewMember,
      updateNewMember,
      removeNewMember,
      setCodedMessage,
      submitCoded,
      setManualCode,
      submitManualCode,
      setReqFirstName,
      setReqLastName,
      setReqEmail,
      setReqPhone,
      setReqMessage,
      addReqExtraMember,
      updateReqExtraMember,
      removeReqExtraMember,
      setReqStatus,
      submitRequest,
      setReqHoneypot,
      setReqTurnstileToken,
    },
  };
}
